import dayjs from 'dayjs';

import {CalendarDay, DateRange, PresetOption} from './types';

// Constants
const DATE_COMPARISON_UNIT = 'day';
const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * Helper function to get preset date ranges
 */
export const getPresetRange = (preset: PresetOption): DateRange => {
  const now = new Date();

  switch (preset) {
    case 'all_time':
      return {
        startDate: new Date(1900, 0, 1), // Far past date
        endDate: now,
      };
    case '1_year':
      return {
        startDate: new Date(
          now.getFullYear() - 1,
          now.getMonth(),
          now.getDate(),
        ),
        endDate: now,
      };
    case '6_months':
      return {
        startDate: new Date(
          now.getFullYear(),
          now.getMonth() - 6,
          now.getDate(),
        ),
        endDate: now,
      };
    case 'this_month':
      return {
        startDate: new Date(now.getFullYear(), now.getMonth(), 1),
        endDate: now,
      };
    case 'custom':
      return {
        startDate: null,
        endDate: null,
      };
    default:
      return {
        startDate: null,
        endDate: null,
      };
  }
};

/**
 * Generate calendar days for a month
 */
export const getDaysInMonth = (year: number, month: number): CalendarDay[] => {
  const results: CalendarDay[] = [];

  // Find the first day of the month
  const firstOfMonth = new Date(year, month, 1);
  // Get last day of month
  const lastOfMonth = new Date(year, month + 1, 0).getDate();

  // Get day of week for first day (0 = Sunday, 6 = Saturday)
  const firstDayOfWeek = firstOfMonth.getDay();

  // Add placeholders for days before the first of the month
  for (let i = 0; i < firstDayOfWeek; i++) {
    const placeholderDate = new Date(year, month, 1 - (firstDayOfWeek - i));
    results.push({
      date: placeholderDate,
      isCurrentMonth: false,
    });
  }

  // Add current month's days
  for (let i = 1; i <= lastOfMonth; i++) {
    results.push({
      date: new Date(year, month, i),
      isCurrentMonth: true,
    });
  }

  // Calculate the day of week for the last day of the month
  const lastDay = new Date(year, month, lastOfMonth);
  const lastDayOfWeek = lastDay.getDay();

  // Add placeholders after the month (to fill out the week)
  const remainingDays = 6 - lastDayOfWeek;
  for (let i = 1; i <= remainingDays; i++) {
    const placeholderDate = new Date(year, month, lastOfMonth + i);
    results.push({
      date: placeholderDate,
      isCurrentMonth: false,
    });
  }

  return results;
};

/**
 * Check if a date is selected
 */
export const isDateSelected = (date: Date, tempRange: DateRange): boolean => {
  if (!tempRange.startDate) return false;

  if (!tempRange.endDate) {
    return dayjs(date).isSame(tempRange.startDate, DATE_COMPARISON_UNIT);
  }

  return (
    dayjs(date).isSame(tempRange.startDate, DATE_COMPARISON_UNIT) ||
    dayjs(date).isSame(tempRange.endDate, DATE_COMPARISON_UNIT)
  );
};

/**
 * Check if a date is in the selected range
 */
export const isDateInRange = (date: Date, tempRange: DateRange): boolean => {
  if (!tempRange.startDate || !tempRange.endDate) return false;

  return date > tempRange.startDate && date < tempRange.endDate;
};

/**
 * Check if a date is today
 */
export const isToday = (date: Date): boolean => {
  return dayjs(date).isSame(new Date(), DATE_COMPARISON_UNIT);
};

export const formatDate = (date: Date): string => {
  return dayjs(date).format(DATE_FORMAT);
};
